import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import React from "react";
import ReactMarkdown from "react-markdown";

// Named rather than the bare `monospace` keyword used for streaming keys: at
// document length the platform UI faces are markedly more readable than the
// browser default, which is usually Courier.
const MONOSPACE_STACK = [
  "ui-monospace",
  "SFMono-Regular",
  "Menlo",
  "Consolas",
  '"Liberation Mono"',
  "monospace",
].join(", ");

interface PolicyBodyProps {
  body?: string | null;
  /** Extra styling for the wrapper, e.g. a bounded scroll region. */
  sx?: SxProps<Theme>;
  "data-testid"?: string;
}

/**
 * Renders a policy document body.
 *
 * The single place this happens, so the public legal page, the re-consent
 * gate, and the superuser preview cannot drift apart - an author has to be
 * able to trust that the preview is what users will see.
 *
 * Markdown is rendered through react-markdown without `rehype-raw`, so raw
 * HTML in a document body is escaped rather than executed.
 *
 * `pre-wrap` on the text blocks preserves the author's line breaks. Markdown
 * collapses a single newline into a space, so a policy typed with one clause
 * per line rendered as an unbroken wall of text. react-markdown does keep the
 * newlines in the DOM (`<p>first\nsecond</p>`), so this is purely a matter of
 * not letting the browser collapse them.
 *
 * Deliberately applied to the text blocks rather than the wrapper: the markup
 * also contains newlines *between* block elements (`</p>\n<p>`), and under
 * `pre-wrap` a wrapper would render each of those as an extra blank line.
 */
function PolicyBody({ body, sx, ...rest }: PolicyBodyProps) {
  return (
    <Box
      data-testid={rest["data-testid"]}
      sx={[
        {
          "& p, & li, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote": {
            whiteSpace: "pre-wrap",
          },
          "& > :first-of-type": { mt: 0 },
          "& > :last-child": { mb: 0 },
          // Long unbroken strings - a URL, an entity name - must not force the
          // whole panel to scroll sideways.
          overflowWrap: "anywhere",
          // Fixed-width, so what the author lays out is what the reader gets:
          // aligned clause numbering, tables of retention periods and the like
          // survive instead of reflowing. Sized down a step because monospace
          // runs visually larger than the proportional face at the same px,
          // and it already costs vertical space by fitting fewer characters
          // per line.
          fontFamily: MONOSPACE_STACK,
          fontSize: "0.875rem",
          lineHeight: 1.6,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ReactMarkdown>{body ?? ""}</ReactMarkdown>
    </Box>
  );
}

export default PolicyBody;
